import React, { useState, useEffect, useCallback } from 'react';
import { postsAPI, categoriesAPI } from '../services/api';
import { Search, Filter, Calendar, Trash2, Edit, ExternalLink, FileText, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../styles/posts.css';

const PostsList = ({ refreshTrigger }) => {
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [selectedIds, setSelectedIds] = useState(new Set());
    
    const [filters, setFilters] = useState({
        page: 1,
        limit: 12,
        status: '',
        category: '',
        search: ''
    });

    const [processingWP, setProcessingWP] = useState({});

    const [confirmModal, setConfirmModal] = useState({ open: false, type: null, post: null });
    const [toast, setToast] = useState({ open: false, type: 'success', message: '' });

    const navigate = useNavigate();

    const loadPosts = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (filters.status) params.status = filters.status;
            if (filters.category) params.category = filters.category;
            if (filters.search) params.search = filters.search;
            params.page = filters.page;
            params.limit = filters.limit;

            const response = await postsAPI.getAllPosts(params);
            const apiData = response.data;
            const postsData = apiData?.data || apiData?.posts || [];
            const apiPagination = apiData?.pagination || {};

            setPosts(postsData);
            setPagination({
                page: apiPagination.current_page ?? filters.page,
                perPage: apiPagination.per_page ?? filters.limit,
                total: apiPagination.total ?? postsData.length,
                totalPages: apiPagination.total_pages ?? 1,
                hasNext: apiPagination.has_next ?? false,
                hasPrev: apiPagination.has_prev ?? false
            });
        } catch (error) {
            console.error('Erro ao carregar posts:', error);
            setPosts([]);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadPosts();
        loadCategories();
    }, [loadPosts, filters, refreshTrigger]);

    const loadCategories = async () => {
        try {
            const response = await categoriesAPI.getAllCategories();
            const data = response?.data?.data ?? response?.data ?? [];
            setCategories(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Erro ao carregar categorias:', error);
            setCategories([]);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: key !== 'page' ? 1 : value // Reset page when other filters change
        }));
    };

    const clearFilters = () => {
        setFilters({ page: 1, limit: 12, status: '', category: '', search: '' });
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };


    const deletePost = async (id, title) => {
        if (!window.confirm(`Tem certeza que deseja deletar o post "${title}"?`)) return;

        try {
            await postsAPI.deletePost(id);
            loadPosts();
            alert('Post deletado com sucesso!');
        } catch (error) {
            alert('Erro ao deletar post: ' + error.message);
        }
    };

    // getStatusColor removido: badge único de status agora usa cores próprias

    const getStatusText = (status) => {
        switch (status) {
            case 'published': return 'Publicado';
            case 'draft': return 'Rascunho';
            case 'scheduled': return 'Agendado';
            default: return status;
        }
    };

    const formatDate = (value) => {
        try {
            if (!value) return '—';
            const d = new Date(value);
            if (isNaN(d.getTime())) return '—';
            return d.toLocaleDateString();
        } catch (_) {
            return '—';
        }
    };

    const getUnifiedStatusBadge = (post) => {
        const wp = post.wp_status;

        let label = 'Rascunho';
        let style = {
            backgroundColor: '#6c757d' // padrão
        };

        if (wp === 'publish' || post.status === 'published') {
            label = 'Publicado';
            style.backgroundColor = '#28a745';
        } else if (wp === 'draft') {
            label = 'Rascunho';
            style = {
                backgroundColor: '#fffdf5',
                border: '1px solid #ffe08a',
                color: '#a67900'
            };
        } else if (wp === 'none') {
            label = 'Salvo';
            style.backgroundColor = '#6c757d';
        }

        return (
            <span className="status-badge unified" style={style}>
                {label}
            </span>
        );
    };

    const openConfirm = (type, post) => {
        setConfirmModal({ open: true, type, post });
    };

    const closeConfirm = () => {
        setConfirmModal({ open: false, type: null, post: null });
    };

    const showToast = (type, message) => {
        setToast({ open: true, type, message });
        setTimeout(() => {
            setToast(prev => ({ ...prev, open: false }));
        }, 3000);
    };

    const sendToWordPressDraft = async (post) => {
        if (processingWP[post.id]) return;
        setProcessingWP(prev => ({ ...prev, [post.id]: true }));
        try {
            const payload = {
                categories: [],
                tags: Array.isArray(post.tags) ? post.tags : [],
                excerpt: post.excerpt || undefined
            };
            const resp = await postsAPI.sendDraftToWordPress(post.id, payload);
            if (resp.data?.success) {
                showToast('success', 'Enviado ao WordPress como rascunho com sucesso!');
                await loadPosts();
            } else {
                const msg = resp.data?.error || 'Erro desconhecido ao enviar ao WordPress';
                showToast('error', msg);
            }
        } catch (error) {
            const msg = error.response?.data?.error || error.message;
            showToast('error', 'Erro ao enviar ao WordPress: ' + msg);
        } finally {
            setProcessingWP(prev => ({ ...prev, [post.id]: false }));
        }
    };

    const publishToWordPress = async (post) => {
        if (processingWP[post.id]) return;
        setProcessingWP(prev => ({ ...prev, [post.id]: true }));
        try {
            const payload = {
                categories: [],
                tags: Array.isArray(post.tags) ? post.tags : [],
                excerpt: post.excerpt || undefined
            };
            const resp = await postsAPI.publishToWordPress(post.id, payload);
            if (resp.data?.success) {
                showToast('success', 'Publicado no WordPress com sucesso!');
                await loadPosts();
            } else {
                const msg = resp.data?.error || 'Erro desconhecido ao publicar no WordPress';
                showToast('error', msg);
            }
        } catch (error) {
            const msg = error.response?.data?.error || error.message;
            showToast('error', 'Erro ao publicar no WordPress: ' + msg);
        } finally {
            setProcessingWP(prev => ({ ...prev, [post.id]: false }));
        }
    };

    const handleConfirm = async () => {
        const { type, post } = confirmModal;
        closeConfirm();
        if (!post) return;
        if (type === 'draft') {
            await sendToWordPressDraft(post);
        } else if (type === 'publish') {
            await publishToWordPress(post);
        }
    };

    console.log('DEBUG → posts:', posts);
    console.log('DEBUG → categories:', categories);
    console.log('DEBUG → pagination:', pagination);
    console.log('DEBUG → filters:', filters);
    console.log('DEBUG → loading:', loading);

    if (loading) {
        return (
            <div className="posts-loading">
                <div className="loading-spinner"></div>
                <p>Carregando posts...</p>
            </div>
        );
    }

    return (
        
        <div className="posts-list">
            <div className="posts-header">
                <h2>📚 Meus Posts</h2>
                <div className="posts-filters">
                    <div className="search-box">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Buscar post por título ou palavra‑chave…"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                        />
                    </div>

                    <div className="filter-group">
                        <Filter size={18} />
                        <div className="filter-item">
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">Todos os Status</option>
                                <option value="published">Publicado</option>
                                <option value="draft">Rascunho</option>
                                <option value="scheduled">Agendado</option>
                            </select>
                        </div>

                        <div className="filter-item">
                            <select
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                            >
                                <option value="">Todas as Categorias</option>
                                {(Array.isArray(categories) ? categories : []).map(category => (
                                    <option key={category.id} value={category.slug}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-item">
                            <select
                                value={filters.limit}
                                onChange={(e) => handleFilterChange('limit', Number(e.target.value))}
                            >
                                <option value={12}>12 por página</option>
                                <option value={24}>24 por página</option>
                                <option value={48}>48 por página</option>
                            </select>
                        </div>
                    </div>

                    {(filters.status || filters.category || filters.search) && (
                        <div className="active-filters">
                            {filters.search && (
                                <span className="chip" title={`Buscar: ${filters.search}`}>Busca: {filters.search}</span>
                            )}
                            {filters.status && (
                                <span className="chip" title={`Status: ${getStatusText(filters.status)}`}>Status: {getStatusText(filters.status)}</span>
                            )}
                            {filters.category && (
                                <span className="chip" title={`Categoria: ${filters.category}`}>Categoria: {filters.category}</span>
                            )}
                            <button className="clear-filters" onClick={clearFilters} title="Limpar filtros">Limpar filtros</button>
                        </div>
                    )}
                </div>
            </div>

            {posts.length === 0 ? (
                <div className="no-posts">
                    <div className="no-posts-icon">📝</div>
                    <h3>Nenhum post encontrado</h3>
                    <p>
                        {filters.status || filters.category || filters.search
                            ? 'Tente ajustar os filtros de busca'
                            : 'Comece gerando seu primeiro artigo com IA!'
                        }
                    </p>
                </div>
            ) : (
                <>
                    <div className="posts-grid">
                        {posts.map(post => (
                            <div key={post.id} className="post-card">
                                <div className="post-card-header">
                                    <div className="post-status">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(post.id)}
                                            onChange={() => toggleSelect(post.id)}
                                            title={selectedIds.has(post.id) ? 'Remover da seleção' : 'Selecionar para ação em massa'}
                                        />
                                        {getUnifiedStatusBadge(post)}
                                    </div>
                                    <div className="post-actions">
                                        {(() => {
                                            const wpRaw = post.wp_status || 'none';
                                            const wp = wpRaw.replace('published', 'publish');
                                            const isNone = wpRaw === 'none';
                                            const isDraft = wpRaw === 'draft' || wp === 'draft';
                                            const isPublished = wpRaw === 'published' || wp === 'publish';

                                            if (isNone) {
                                                return (
                                                    <>
                                                        <button
                                                            className="icon-btn wp-draft"
                                                            onClick={() => openConfirm('draft', post)}
                                                            title="Enviar ao WordPress (rascunho)"
                                                            aria-label="Enviar ao WordPress (rascunho)"
                                                            disabled={!!processingWP[post.id]}
                                                        >
                                                            <FileText size={16} />
                                                        </button>
                                                        <button
                                                            className="icon-btn wp-publish"
                                                            onClick={() => openConfirm('publish', post)}
                                                            title="Publicar no WordPress"
                                                            aria-label="Publicar no WordPress"
                                                            disabled={!!processingWP[post.id]}
                                                        >
                                                            <Send size={16} />
                                                        </button>
                                                    </>
                                                );
                                            }

                                            if (isDraft) {
                                                return (
                                                    <button
                                                        className="icon-btn wp-publish"
                                                        onClick={() => openConfirm('publish', post)}
                                                        title="Mover para publicado no WordPress"
                                                        aria-label="Mover para publicado no WordPress"
                                                        disabled={!!processingWP[post.id]}
                                                    >
                                                        <Send size={16} />
                                                    </button>
                                                );
                                            }

                                            if (isPublished) {
                                                return (
                                                    <button
                                                        className="icon-btn wp-draft"
                                                        onClick={() => openConfirm('draft', post)}
                                                        title="Mover para rascunho no WordPress"
                                                        aria-label="Mover para rascunho no WordPress"
                                                        disabled={!!processingWP[post.id]}
                                                    >
                                                        <FileText size={16} />
                                                    </button>
                                                );
                                            }

                                            return null;
                                        })()}
                                        {post.wp_link && (
                                            <a
                                                className="icon-btn"
                                                href={post.wp_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Abrir no WordPress"
                                                style={{ textDecoration: 'none' }}
                                            >
                                                <ExternalLink size={16} />
                                            </a>
                                        )}
                                        <button 
                                            className="icon-btn"
                                            onClick={() => navigate(`/posts/${post.id}/edit`)}
                                            title="Editar post"
                                        >
                                            <Edit size={16} />
                                        </button>
                                        <button 
                                            className="icon-btn danger"
                                            onClick={() => deletePost(post.id, post.title)}
                                            title="Deletar post"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>

                                <div className="post-content">
                                    <h3 className="post-title">{post.title}</h3>
                                    <div className="post-date">
                                        <Calendar size={14} />
                                        <span>{formatDate(post.created_at)}</span>
                                    </div>
                                    <p className="post-excerpt">{post.excerpt || 'Sem resumo disponível'}</p>
                                </div>

                                

                                <div className="post-footer">
                                    <a 
                                        className="read-more" 
                                        href={`/posts/${post.id}/edit`}
                                        title="Ver detalhes"
                                    >
                                        Ler mais <ExternalLink size={14} />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="pagination">
                        <div className="page-info-left">
                            {(() => {
                                const start = (pagination.page - 1) * pagination.perPage + 1;
                                const end = Math.min(start + pagination.perPage - 1, pagination.total || posts.length);
                                const total = pagination.total || posts.length;
                                return <span className="page-range">Exibindo {start}–{end} de {total} posts</span>;
                            })()}
                        </div>
                        <button 
                            disabled={!pagination.hasPrev}
                            onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                            className="page-btn"
                        >
                            ← Anterior
                        </button>
                        <span className="page-info">
                            Página {pagination.page} de {pagination.totalPages}
                        </span>
                        <button 
                            disabled={!pagination.hasNext}
                            onClick={() => handleFilterChange('page', filters.page + 1)}
                            className="page-btn"
                        >
                            Próxima →
                        </button>
                        <div className="page-size">
                            <select value={filters.limit} onChange={(e) => handleFilterChange('limit', Number(e.target.value))}>
                                <option value={12}>12</option>
                                <option value={24}>24</option>
                                <option value={48}>48</option>
                            </select>
                            <span>por página</span>
                        </div>
                    </div>
                </>
            )}

            {confirmModal.open && (
                <div className="modal-overlay" onClick={closeConfirm}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{confirmModal.type === 'publish' ? 'Confirmar publicação' : 'Enviar como rascunho'}</h3>
                        <p>
                            Tem certeza que deseja {confirmModal.type === 'publish' ? 'publicar' : 'enviar como rascunho'} o post "{confirmModal.post?.title}" no WordPress?
                        </p>
                        <div className="modal-actions">
                            <button className="btn cancel" onClick={closeConfirm}>Cancelar</button>
                            <button className="btn confirm" onClick={handleConfirm}>Confirmar</button>
                        </div>
                    </div>
                </div>
            )}

            {toast.open && (
                <div className={`toast ${toast.type}`}>
                    {toast.message}
                </div>
            )}
        </div>
    );
};

export default PostsList;